// 📁 src/admin/Orders.js
import React, { useEffect, useState } from "react";
import { api } from "../axiosClient";
import moment from "moment";
import { useCurrency } from "../context/CurrencyContext";

const statusOptions = [
  "Order Placed",
  "Order Confirmed",
  "Preparing for Dispatch",
  "Ready to be Shipped",
  "Shipped",
  "In Transit",
  "Out for Delivery",
  "Delivered",
  "Delivery Attempted",
  "Delivery Failed",
  "Order Cancelled",
  "Refund Initiated",
  "Refunded",
];

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const { rates } = useCurrency();

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const res = await api.get("/api/orders");
        setOrders(Array.isArray(res.data) ? res.data : []);
      } catch (err) { 
        console.error("Failed to fetch orders:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/api/orders/${id}/status`, { status });
      setOrders((prev) =>
        prev.map((order) => (order._id === id ? { ...order, status } : order))
      );
    } catch (err) {
      console.error("Failed to update order status:", err);
    }
  };

  const getSymbol = (currency) => {
    switch (currency) {
      case "INR":
        return "₹";
      case "USD":
        return "$";
      case "EUR":
        return "€";
      case "GBP":
        return "£";
      case "AED":
        return "د.إ";
      default:
        return "";
    }
  };

  const filteredOrders = orders.filter((order) => {
  const text = search.toLowerCase();
  const matchesSearch =
    order._id?.toLowerCase().includes(text) ||
    order.fullName?.toLowerCase().includes(text) ||
    order.email?.toLowerCase().includes(text) ||
    order.phone?.toLowerCase().includes(text);

  const matchesStatus =
    statusFilter === "" ||
    order.status?.toLowerCase().trim() === statusFilter.toLowerCase().trim();

  return matchesSearch && matchesStatus;
});

  if (loading) return <p>Loading orders...</p>;

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold mb-4">All Orders</h1>

      {/* 🟢 Search & Status Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email, phone, or Order ID"
          className="border p-2 rounded w-full sm:max-w-md"
        />

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border p-2 rounded w-full sm:w-64"
        >
          <option value="">All Statuses</option>
          {statusOptions.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm border border-[#236e47] bg-white shadow-md">
          <thead className="bg-[#105132] text-white">
            <tr>
              <th className="p-2">Date</th>
              <th className="p-2 text-left">Order ID</th>
              <th className="p-2">Customer</th>
              <th className="p-2">Phone</th>
              <th className="p-2">Total</th>
              <th className="p-2">Items</th>
              <th className="p-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.map((order, index) => {
              const symbol = getSymbol(order.currency);
              const rate = rates?.[order.currency] || 1;

              const total = order.totalConverted ?? order.total;
              const subtotal = order.subtotalConverted ?? order.subtotal;
              const discount = order.discountConverted ?? order.discount;
              const delivery = order.deliveryConverted ?? order.delivery;

              return (
                <tr
                  key={order._id}
                  className={`border-t border-[#236e47]/30 text-sm ${
                    index % 2 === 0 ? "bg-[#f9f9f3]" : "bg-white"
                  }`}
                >
                  <td className="p-3 text-center text-sm text-gray-600 font-medium whitespace-nowrap">
                    {moment(order.createdAt).format("DD MMM YYYY")}
                  </td>

                  <td className="p-3 font-mono text-xs text-gray-600">
                    {order._id}
                  </td>

                  <td className="p-3">
                    <div className="font-semibold text-[#105132]">
                      {order.fullName}
                    </div>
                    <div className="text-xs text-gray-500">{order.email}</div>
                  </td>

                  <td className="p-3">
                    <div className="text-base">{order.phone}</div>
                  </td>

                  <td className="p-3">
                    <div className="font-bold text-lg text-[#105132]">
                      {symbol}
                      {total?.toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-500">
                      Subtotal: {symbol}
                      {subtotal?.toFixed(2)}
                      <br />
                      Discount: {symbol}
                      {discount?.toFixed(2)}
                      <br />
                      Delivery: {symbol}
                      {delivery?.toFixed(2)}
                    </div>
                  </td>

                  <td className="p-3 max-w-[250px]">
                    <div className="space-y-1">
                      {order.cart?.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex justify-between text-xs text-gray-700 border-b border-dashed pb-1"
                        >
                          <span className="truncate">
                            {item.name} × {item.quantity}
                          </span>
                          <span>
                            {symbol}
                            {((item.price * item.quantity) * rate).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </td>

                  <td className="p-3">
                    <select
                      value={order.status || "Order Placed"}
                      onChange={(e) =>
                        updateStatus(order._id, e.target.value)
                      }
                      className="border p-2 rounded text-xs bg-white hover:bg-gray-50"
                    >
                      {statusOptions.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}

            {filteredOrders.length === 0 && (
              <tr>
                <td colSpan="7" className="text-center p-4 text-gray-500">
                  No matching orders found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Orders;
